// src/components/common/OrderNotification.jsx
import { useState, useCallback, useEffect } from 'react';
import { useApp } from '../../context/AppContext';

// 之前用 AudioContext + createOscillator 现场合成音效这条路，反复测试都没能验证成功过。
// 改回用 new Audio() 播放实际的音频文件——这个做法之前在同样的环境下已经验证有效过，
// 不再继续在没有把握的技术路线上排查。两个文件复用你之前已经准备好的：
// new-order.mp3（新订单）、payment-success.wav（支付成功 + 已取消但已付款这个警告场景暂时共用）
function playSound(src) {
  try {
    const audio = new Audio(src);
    audio.play().catch(err => console.warn('[提示音] 播放失败:', err));
  } catch (err) {
    console.warn('[提示音] 初始化失败:', err);
  }
}

function playNotifySound() {
  playSound('/sounds/new-order.mp3');
}

function playPaymentSuccessSound() {
  playSound('/sounds/payment-success.wav');
}

// 警告音（订单已取消但客户又付款了这种边缘情况）——暂时复用支付成功那段音频，
// 如果想要一段区分度更高的声音，单独准备一份放进 public/sounds/ 目录，告诉我文件名接进来
function playWarningSound() {
  playSound('/sounds/payment-success.wav');
}

// 根据消息类型直接决定放哪个声音，不再依赖一个容易漏传的布尔参数——
// 之前"支付成功"这个类型漏传了 isWarning，导致一直在误用"新订单"那个音效
function playSoundForType(type) {
  if (type === 'NEW_ORDER' || type === 'ORDER_ASSIGNED') {
    playNotifySound();
  } else if (type === 'KHQR_PAID') {
    playPaymentSuccessSound();
  } else if (type === 'KHQR_PAID_BUT_CANCELLED') {
    playWarningSound();
  }
  // KHQR_FAILED / ORDER_UPDATED 目前不响铃，只显示视觉通知卡片
}

const TYPE_CONFIG = {
  NEW_ORDER: {
    icon: '🛍️', color: 'from-orange-500 to-amber-500',
    border: 'border-orange-400', label: '新订单',
  },
  ORDER_ASSIGNED: {
    icon: '📋', color: 'from-blue-500 to-indigo-500',
    border: 'border-blue-400', label: '新指派',
  },
  ORDER_UPDATED: {
    icon: '🔄', color: 'from-green-500 to-emerald-500',
    border: 'border-green-400', label: '订单更新',
  },
  KHQR_PAID: {
    icon: '💰', color: 'from-green-500 to-teal-500',
    border: 'border-green-400', label: 'KHQR支付成功',
  },
  KHQR_FAILED: {
    icon: '⏰', color: 'from-gray-400 to-gray-500',
    border: 'border-gray-300', label: '支付超时',
  },
  KHQR_PAID_BUT_CANCELLED: {
    icon: '⚠️', color: 'from-red-500 to-orange-500',
    border: 'border-red-400', label: '⚠️ 支付异常',
  },
};

const NotifyCard = ({ note, onClose }) => {
  const [visible, setVisible] = useState(false);
  const cfg = TYPE_CONFIG[note.type] || TYPE_CONFIG.NEW_ORDER;
  const isWarning = note.type === 'KHQR_PAID_BUT_CANCELLED';

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 10);
    return () => clearTimeout(t);
  }, []);

  const handleClose = () => {
    setVisible(false);
    setTimeout(() => onClose(note.id), 300);
  };

  return (
    <div className={`
      flex items-start gap-3 bg-white rounded-2xl shadow-2xl border ${cfg.border}
      px-4 py-3 transition-all duration-300
      ${isWarning ? 'w-96' : 'w-80'}
      ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-full'}
    `}>
      <div className={`w-1 self-stretch rounded-full bg-gradient-to-b ${cfg.color} flex-shrink-0`} />
      <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${cfg.color} flex items-center justify-center flex-shrink-0 shadow-md`}>
        <span className="text-lg">{cfg.icon}</span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-0.5">
          <span className={`text-xs font-bold uppercase tracking-wide ${isWarning ? 'text-red-500' : 'text-gray-400'}`}>
            {cfg.label}
          </span>
          <span className="text-xs text-gray-300">{note.time}</span>
        </div>
        <p className={`text-sm font-semibold truncate ${isWarning ? 'text-red-700' : 'text-gray-800'}`}>
          {note.title}
        </p>
        {note.sub && (
          <p className={`text-xs truncate mt-0.5 ${isWarning ? 'text-red-500 whitespace-normal' : 'text-gray-500'}`}>
            {note.sub}
          </p>
        )}
        {isWarning && (
          <p className="text-xs text-red-400 mt-1 font-medium">
            请手动补录此笔交易或联系管理员处理退款
          </p>
        )}
      </div>
      <button onClick={handleClose}
        className={`flex-shrink-0 w-5 h-5 flex items-center justify-center transition-colors mt-0.5 ${
          isWarning ? 'text-red-300 hover:text-red-500' : 'text-gray-300 hover:text-gray-500'
        }`}>
        ✕
      </button>
    </div>
  );
};

const OrderNotification = () => {
  const { addWsListener } = useApp();
  const [notes, setNotes] = useState([]);

  const removeNote = useCallback((id) => {
    setNotes(prev => prev.filter(n => n.id !== id));
  }, []);

  const addNote = useCallback((type, title, sub) => {
    playSoundForType(type);
    const time = new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' });
    const id   = Date.now() + Math.random();
    setNotes(prev => [{ id, type, title, sub, time }, ...prev].slice(0, 5));
  }, []);

  useEffect(() => {
    const unsub = addWsListener((msg) => {

      if (msg.type === 'NEW_ORDER' && msg.order) {
        const o = msg.order;
        addNote('NEW_ORDER',
          `新订单 #${o.order_no || o.id}`,
          `$${parseFloat(o.total_amount || 0).toFixed(2)} · ${o.delivery_type === 'pickup' ? '自提' : '配送'}`
        );
      }

      if (msg.type === 'ORDER_ASSIGNED' && msg.order) {
        const o = msg.order;
        addNote('ORDER_ASSIGNED',
          `收到新指派订单 #${o.order_no || o.id}`,
          `$${parseFloat(o.total_amount || 0).toFixed(2)}`
        );
      }

      if (msg.type === 'ORDER_UPDATED' && msg.order) {
        const statusMap = { confirmed: '分店已接单', cancelled: '订单已取消', completed: '订单已完成' };
        if (statusMap[msg.order.order_status]) {
          addNote('ORDER_UPDATED',
            `${statusMap[msg.order.order_status]} #${msg.order.order_no || msg.order.id}`,
            null
          );
        }
      }

      if (msg.type === 'KHQR_PAID' && msg.data) {
        addNote('KHQR_PAID',
          `KHQR支付成功 #${msg.data.order_no}`,
          `$${parseFloat(msg.data.total_amount || 0).toFixed(2)}`
        );
      }

      if (msg.type === 'KHQR_FAILED' && msg.data) {
        addNote('KHQR_FAILED',
          `支付超时 #${msg.data.order_no}`,
          null
        );
      }

      if (msg.type === 'KHQR_PAID_BUT_CANCELLED' && msg.data) {
        addNote(
          'KHQR_PAID_BUT_CANCELLED',
          `订单已关闭但客户已付款！`,
          `订单 ${msg.data.order_no} · $${parseFloat(msg.data.total_amount || 0).toFixed(2)}`
        );
      }

    });
    return unsub;
  }, [addWsListener, addNote]);

  if (notes.length === 0) return null;

  return (
    <div className="fixed top-20 right-4 z-50 flex flex-col gap-2 pointer-events-none">
      {notes.map(note => (
        <div key={note.id} className="pointer-events-auto">
          <NotifyCard note={note} onClose={removeNote} />
        </div>
      ))}
    </div>
  );
};

export default OrderNotification;